import React, { FC } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useToast } from '@common/toast';
import Icon from '@components/common/assets';
import { useDuplicateStudentIdCheck, useSaveAffiliatedUniversityInfo } from '@repository/partnership/usePartnership';
import { useRenewAccessTokenRefreshToken } from '@repository/auth/useUserInfo';
import School from './School';
import Major from './Major';
import StudentId from './StudentId';
import EmailAuth from './EmailAuth';
import TermsFrom from './TermsFrom';
import AffiliatedUniversitySaveRq from '@domain/rq/alliance/AffiliatedUniversityRq';
import { AffiliatedUniversityInfoSaveVO } from '@domain/vo/alliance/AffiliatedUniversityInfoVO';
import { KeyboardEventUtil } from '@utils/KeyboardEventUtil';

import classnames from 'classnames/bind';
import styles from '../premium.module.scss';

const cx = classnames.bind(styles);

interface AffiliatedUniversityFormProps {
  refetchData: () => void;
}

const AffiliatedUniversityForm: FC<AffiliatedUniversityFormProps> = ({ refetchData }) => {
  const Toast = useToast();
  const { mutateAsync: fetchRefreshToken } = useRenewAccessTokenRefreshToken();
  const { mutateAsync: checkDuplicateStudentId } = useDuplicateStudentIdCheck();
  const { mutateAsync: saveAffiliatedUniversityInfo } = useSaveAffiliatedUniversityInfo();

  const methods = useForm<AffiliatedUniversityInfoSaveVO>({
    mode: 'onChange',
  });

  const {
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = methods;

  const isDisabledSubmitBtn = !(
    Object.keys(errors).length < 1 &&
    watch('emailCheck') &&
    watch('major') &&
    watch('school') &&
    watch('studentId') &&
    watch('agreePrivacyCollectionYn' as any) &&
    watch('agreePrivacyProvisionYn' as any)
  );

  const onCheckDuplicateStudentId = async (data: AffiliatedUniversityInfoSaveVO) => {
    const { school, studentId } = data;
    const schoolCode = typeof school.value === 'string' ? parseInt(school.value) : school.value;
    try {
      const result = await checkDuplicateStudentId({ schoolCode, studentId });

      if (result.isDuplicated) setError('studentId', { message: '중복된 학번 정보입니다.' });
      return result.isDuplicated;
    } catch (e: any) {
      switch (e?.response?.data.errorCode) {
        case 'B901':
          Toast({ type: 'error', content: '다시 시도해주세요' });
          break;
        case 'B902':
          setError('studentId', { message: '올바른 학번 형식이 아닙니다.' });
          break;
        case 'B903':
          setError('school', { message: '존재하지 않는 제휴 대학 입니다.' });
          break;
        case 'B904':
          setError('studentId', { message: '중복된 학번 정보입니다.' });
          break;
      }

      return true;
    }
  };

  const reload = async () => {
    //제휴 서비스 인증 권한 추가 하기 위한 토큰 재발급/사용자 정보 reload
    await fetchRefreshToken();
    refetchData();
  };

  const onSubmit = async (data: AffiliatedUniversityInfoSaveVO) => {
    const isError = await onCheckDuplicateStudentId(data);
    if (isError) return;

    const rq = new AffiliatedUniversitySaveRq(data);
    const result = await saveAffiliatedUniversityInfo(rq);

    if (result) {
      Toast({
        iconType: 'check',
        type: 'success',
        content: '인증이 완료되었습니다.',
      });
      await reload();
    } else {
      Toast({ type: 'error', iconType: 'info', content: '제휴서비스 신청이 실패되었습니다.' });
    }
  };

  return (
    <div className={cx('contentsWrap')}>
      <div className={cx('requireTextArea')}>
        {/* 폼 링크 이동 */}
        <a className={cx('formLink')} href={'https://forms.office.com/r/QfCfGwfgBN'} target={'_blank'}>
          나의 학교, 기관이 제휴 리스트에 없나요?
          <Icon name={'arrowRightLight'} width={28} height={28} />
        </a>
      </div>
      <FormProvider {...methods}>
        <form
          autoComplete={'off'}
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={KeyboardEventUtil.onCheckKeyDownPreventEnter}
        >
          <School name={'school'} />
          <Major name={'major'} />
          <StudentId name={'studentId'} />
          <EmailAuth />
          <TermsFrom />
          <div className={cx('btnWrap')}>
            <button className={cx('btnSave')} type={'submit'} disabled={isDisabledSubmitBtn}>
              제휴 서비스 신청
            </button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default AffiliatedUniversityForm;
